import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getSafeRedirect } from '@/lib/authRedirect';
import { SplitAuthLayout } from '@/layouts/SplitAuthLayout';
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons';
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';

const inputChrome =
  'h-12 w-full rounded-[10px] border border-warm-border bg-paper px-[14px] text-[15px] text-ink placeholder:text-ink-3 transition-all duration-[120ms] focus:border-coral focus:outline-none focus:ring-[3px] focus:ring-coral/15 disabled:cursor-not-allowed';

const eyebrowLabel =
  'font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-ink-2';

const EyeIcon = ({ open }: { open: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx={12} cy={12} r={3} />
    {!open && <path d="M3 3l18 18" />}
  </svg>
);

const AlertIcon = ({
  color = 'currentColor',
  size = 18,
}: {
  color?: string;
  size?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="mt-[1px] shrink-0"
  >
    <circle cx={12} cy={12} r={9} />
    <path d="M12 8v5" />
    <path d="M12 16.5h.01" />
  </svg>
);

const Spinner = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    style={{ animation: 'core-spin 0.8s linear infinite' }}
  >
    <circle cx={12} cy={12} r={9} stroke="#FFFFFF" strokeOpacity={0.25} strokeWidth={2.5} />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="#FFFFFF" strokeWidth={2.5} strokeLinecap="round" />
  </svg>
);

export default function SignupPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmEmailAddress, setConfirmEmailAddress] = useState<string | null>(null);

  const passwordsMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      const { error: err, confirmEmail } = await signUp(email, password);
      if (err) {
        setError(err.message);
      } else if (confirmEmail) {
        setConfirmEmailAddress(email);
      } else {
        navigate(getSafeRedirect(searchParams.get('redirect'), '/onboarding'), {
          replace: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Email confirmation success state — design's ConfirmScreen
  if (confirmEmailAddress) {
    return (
      <SplitAuthLayout>
        <div className="flex flex-col gap-6 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-teal-dark bg-teal-light">
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="var(--color-teal-dark)"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="m-0 font-display text-[32px] font-normal leading-[1.05] tracking-[-0.02em] text-ink">
              Check your email
            </h2>
            <p className="m-0 text-[15px] leading-[1.5] text-ink-2">
              We've sent a confirmation link to{' '}
              <span className="font-medium text-ink">{confirmEmailAddress}</span>.
              Click the link to activate your account.
            </p>
          </div>
          <Button
            type="button"
            className="h-12 w-full rounded-[10px] border border-coral-dark bg-coral text-[15px] font-medium tracking-[-0.005em] text-white shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,_0_1px_1px_rgba(154,78,58,0.25)] hover:bg-coral-hover"
            onClick={() => navigate('/login')}
          >
            Back to sign in
          </Button>
        </div>
      </SplitAuthLayout>
    );
  }

  return (
    <SplitAuthLayout>
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-2">
          <h2 className="m-0 font-display text-[32px] font-normal leading-[1.1] tracking-[-0.02em] text-ink">
            Create your account
          </h2>
          <p className="m-0 text-[15px] leading-[1.5] text-ink-2">
            Start building your Core workspace.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-[10px] border border-[#C8553D]/30 bg-[#FBEEEA] px-[14px] py-3"
          >
            <AlertIcon color="#C8553D" />
            <div className="flex flex-col gap-[3px]">
              <p className="m-0 text-[13.5px] font-medium leading-[1.3] text-[#7A2E1F]">
                We couldn't create your account.
              </p>
              <p className="m-0 text-[13px] leading-[1.45] text-[#8A4A3A]">
                {error}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-7">
          <div
            className={`flex flex-col gap-[14px] ${isLoading ? 'pointer-events-none opacity-70' : ''}`}
          >
            <label className="flex flex-col gap-1.5">
              <span className={eyebrowLabel}>Work Email</span>
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
                autoFocus
                autoComplete="email"
                className={inputChrome}
              />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className={eyebrowLabel}>Password</span>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Choose a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  autoComplete="new-password"
                  className={`${inputChrome} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-3 transition-colors hover:text-ink-2"
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
              <PasswordStrengthIndicator password={password} showRules />
            </label>

            <label className="flex flex-col gap-1.5">
              <span className={eyebrowLabel}>Confirm Password</span>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  autoComplete="new-password"
                  className={`${inputChrome} pr-11`}
                  style={
                    passwordsMismatch
                      ? { borderColor: '#C8553D' }
                      : undefined
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  tabIndex={-1}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-3 transition-colors hover:text-ink-2"
                >
                  <EyeIcon open={showConfirm} />
                </button>
              </div>
              {passwordsMismatch && (
                <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[#C8553D]">
                  <AlertIcon color="#C8553D" size={12} />
                  Passwords don't match.
                </span>
              )}
            </label>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="h-12 w-full gap-2.5 rounded-[10px] border border-coral-dark bg-coral text-[15px] font-medium tracking-[-0.005em] text-white shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,_0_1px_1px_rgba(154,78,58,0.25)] hover:bg-coral-hover disabled:cursor-not-allowed disabled:opacity-90"
          >
            {isLoading ? (
              <>
                <Spinner />
                Creating account…
              </>
            ) : (
              'Create account'
            )}
          </Button>
        </form>

        <div className="flex items-center gap-3">
          <Separator className="flex-1 bg-warm-border" />
          <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-3">
            or
          </span>
          <Separator className="flex-1 bg-warm-border" />
        </div>

        <SocialLoginButtons />

        <p className="m-0 text-center text-[12px] leading-[1.5] text-ink-3">
          By creating an account, you agree to our{' '}
          <Link
            to="/terms"
            className="font-medium text-ink-2 transition-colors hover:text-ink"
          >
            Terms
          </Link>{' '}
          and{' '}
          <Link
            to="/privacy"
            className="font-medium text-ink-2 transition-colors hover:text-ink"
          >
            Privacy Policy
          </Link>
          .
        </p>

        <p className="m-0 text-center text-[13px] text-ink-3">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-medium text-coral-dark no-underline border-b border-coral-dark/30 hover:border-coral-dark"
          >
            Sign in
          </Link>
        </p>
      </div>
    </SplitAuthLayout>
  );
}
